import type {
  ColoredMaterial,
  SalesOpportunity,
} from "@color-finder/color-finder-api/models";
import { Button, type ButtonProps } from "@mui/material";
import { FullScreenLoader } from "components/loader/FullScreenLoader";
import { getAuthenticatedAxios, useAuthUser } from "hooks/authentication";
import { type Dispatch, type FC, useState } from "react";
import {
  createDigitalSampleRequest,
  type DigitalSampleRequestCreateResponse,
} from "service/create-digital-sample.request";
import { useAppConfig } from "../../../config/app-config";
import { logCustomEvent } from "../../../datadog";
import { attachDigitalSampleRequestToOpportunity } from "../../../service/sales-opportunity";
import { DigitalSampleRequestCompletedDialog } from "./DigitalSampleRequestCompletedDialog";
import { DigitalSampleRequestDialog } from "./DigitalSampleRequestDialog";

export interface DigitalSampleRequestCreateProps extends ButtonProps {
  material: ColoredMaterial;
  onDigitalSampleRequestSubmit?: Dispatch<void>;
}

export const DigitalSampleRequestButton: FC<
  DigitalSampleRequestCreateProps
> = ({ onDigitalSampleRequestSubmit, material, sx, ...props }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<DigitalSampleRequestCreateResponse>();
  const { axios } = getAuthenticatedAxios();
  const { user } = useAuthUser();
  const [appConfig] = useAppConfig();

  const handleClickOpen = () => {
    logCustomEvent("digital-sample-request-start", {
      material_grade: material.material.shortGrade,
      elj_experiment_id: material.eljExperimentId,
    });
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleSubmit = async (data: {
    finish: string;
    comment?: string;
    requester?: string;
    opportunity: SalesOpportunity;
  }) => {
    if (!material.material.shortGrade) {
      return;
    }

    setOpen(false);
    setSaving(true);
    const attachmentRequest = appConfig.salesOpportunities.enabled
      ? attachDigitalSampleRequestToOpportunity(
          axios,
          data.opportunity,
          material,
          data.finish,
          data.comment,
        )
      : undefined;
    const resultRequest = createDigitalSampleRequest(axios, {
      requester: data.requester,
      comment: data.comment,
      materialGrade: material.material.shortGrade,
      eljExperimentId: material.eljExperimentId,
      eljExperimentBatch: material.eljExperimentBatch,
      materialColorCode: material.color.finalColorCode,
      preliminaryColorCode: material.color.preliminaryColorCode,
      materialFinish: data.finish,
    });

    const [result] = await Promise.all([resultRequest, attachmentRequest]);
    onDigitalSampleRequestSubmit?.();
    setSaving(false);
    setResult(result);
  };

  const handleAlertClose = () => {
    setResult(undefined);
  };

  if (material.availability.colorStatus !== "ACTIVE") {
    return null;
  }

  return (
    <>
      <Button
        onClick={handleClickOpen}
        sx={{ padding: 0, height: "auto", ...sx }}
        {...props}
      >
        Request creation of a Digital Sample
      </Button>
      <FullScreenLoader open={saving} />
      <DigitalSampleRequestDialog
        open={open}
        onClose={handleClose}
        material={material}
        userEmail={user?.email}
        onSubmit={handleSubmit}
      />
      <DigitalSampleRequestCompletedDialog
        open={result !== undefined}
        onClose={handleAlertClose}
        response={result}
      />
    </>
  );
};
